import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import { notificationQueryKeys } from '@/hooks/queries/use-notifications-query';
import type {
  ApiResponse,
  DismissNotificationResponse,
  MarkNotificationReadResponse,
  MarkNotificationsReadRequest,
  MarkNotificationsReadResponse,
  NotificationTab,
} from '@fluenceur/types';
import { NotificationTab as NotificationTabValue } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

type NotificationActionError = AxiosError<ApiResponse<unknown>>;

async function markNotificationRead(
  notificationId: string
): Promise<ApiResponse<MarkNotificationReadResponse>> {
  const response = await api.post<ApiResponse<MarkNotificationReadResponse>>(
    `/dashboard/notifications/${notificationId}/read`
  );

  return response.data;
}

async function markAllNotificationsRead(
  tab: NotificationTab
): Promise<ApiResponse<MarkNotificationsReadResponse>> {
  const payload: MarkNotificationsReadRequest = tab === NotificationTabValue.ALL ? {} : { tab };
  const response = await api.post<ApiResponse<MarkNotificationsReadResponse>>(
    '/dashboard/notifications/read-all',
    payload
  );

  return response.data;
}

async function dismissNotification(
  notificationId: string
): Promise<ApiResponse<DismissNotificationResponse>> {
  const response = await api.post<ApiResponse<DismissNotificationResponse>>(
    `/dashboard/notifications/${notificationId}/dismiss`
  );

  return response.data;
}

function useNotificationInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
  };
}

export function useMarkNotificationReadAction() {
  const { t } = useTranslation();
  const invalidateNotifications = useNotificationInvalidation();

  return useMutation<ApiResponse<MarkNotificationReadResponse>, NotificationActionError, string>({
    mutationFn: markNotificationRead,
    onSuccess: invalidateNotifications,
    onError: () => {
      toaster.error({
        title: t('common.error'),
        description: t('notifications.feedback.actionError'),
      });
    },
  });
}

export function useMarkAllNotificationsReadAction() {
  const { t } = useTranslation();
  const invalidateNotifications = useNotificationInvalidation();

  return useMutation<
    ApiResponse<MarkNotificationsReadResponse>,
    NotificationActionError,
    NotificationTab
  >({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      invalidateNotifications();
      toaster.success({
        title: t('notifications.feedback.markAllReadSuccess'),
      });
    },
    onError: () => {
      toaster.error({
        title: t('common.error'),
        description: t('notifications.feedback.actionError'),
      });
    },
  });
}

export function useDismissNotificationAction() {
  const { t } = useTranslation();
  const invalidateNotifications = useNotificationInvalidation();

  return useMutation<ApiResponse<DismissNotificationResponse>, NotificationActionError, string>({
    mutationFn: dismissNotification,
    onSuccess: invalidateNotifications,
    onError: () => {
      toaster.error({
        title: t('common.error'),
        description: t('notifications.feedback.actionError'),
      });
    },
  });
}
